import React, {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useState,
} from 'react';
import type {TableProps as TempoTableProps} from '@tempo/core';
import {Table as TempoTable, useBreakpointValue} from '@tempo/core';
import type {Breakpoint} from '@deezer/next/services/types/breakpoints';
import type {TableContextValue, TableSort, Column} from './types';
import {countVisibleColumns} from '@components/Table/utils';

import type {
	D4CTableSize,
	D4CTableVariant,
} from '@deezer/next/shared/theme/components/D4CTable';
import {resolveD4CTableStyles} from '@deezer/next/shared/theme/components/D4CTable';
import {TableStyleContext} from './contexts/TableStyleContext';

export type TableContextType<T = string> = React.Context<TableContextValue<T>>;

export const TableContext = createContext<TableContextValue>({
	columns: [],
	columnCount: 0,
	variant: undefined,
	size: undefined,
	sort: undefined,
	setSort: () => {},
});

export const useTableContext = <T extends string = string>() => {
	return useContext<TableContextValue>(
		TableContext,
	) as unknown as TableContextValue<T>;
};

type TableProps = {
	columns: Column[];
	children: React.ReactNode;
	stickyHeader?: boolean;
	['data-testid']?: string;
} & TempoTableProps & {
		variant?: D4CTableVariant;
		size?: D4CTableSize;
	};

function mergeSx(...styles: Array<Record<string, any> | undefined>) {
	return Object.assign({}, ...styles.filter(Boolean));
}

const Table = ({
	children,
	columns,
	stickyHeader,
	variant,
	size = 'sm',
	sx,
	...rest
}: TableProps) => {
	const currentBreakpoint = useBreakpointValue({
		base: 'base',
		sm: 'sm',
		md: 'md',
		lg: 'lg',
		xl: 'xl',
		'2xl': '2xl',
	}) as Breakpoint;

	const visibleColumnCount = useMemo(
		() => countVisibleColumns(columns, currentBreakpoint),
		[columns, currentBreakpoint],
	);

	const [sort, setSort] = useState<TableSort | undefined>();

	const handleSort = useCallback((key: string) => {
		setSort(prev => {
			if (!prev || prev.key !== key) {
				return {key, asc: true};
			}

			if (prev.asc) {
				return {key, asc: false};
			}

			return undefined;
		});
	}, []);

	const contextValue = useMemo(
		() => ({
			columns,
			stickyHeader,
			variant,
			size,
			sort,
			setSort: handleSort,
			columnCount: visibleColumnCount,
		}),
		[
			columns,
			stickyHeader,
			variant,
			size,
			sort,
			handleSort,
			visibleColumnCount,
		],
	);

	const recipeStyles = useMemo(
		() => resolveD4CTableStyles(size, variant),
		[size, variant],
	);

	return (
		<TableContext.Provider value={contextValue}>
			<TableStyleContext.Provider value={recipeStyles}>
				<TempoTable {...rest} sx={mergeSx(recipeStyles.table, sx)}>
					{children}
				</TempoTable>
			</TableStyleContext.Provider>
		</TableContext.Provider>
	);
};

export default Table;
